import React, { useEffect } from 'react';
import DeleteModal from '../settings/delete-modal';
import { useDeleteFolder } from './use-delete-folder';
import { ITripFolder } from '../../../Models/trips/trip-folder.model';

type Props = {
  show: boolean;
  handleClose: () => void;
  folder: ITripFolder | null;
  onSuccess?: () => void;
};

const DeleteFolderModal = ({ show, handleClose, folder, onSuccess }: Props) => {
  const {
    deleteFolder,
    isDeleting,
    error,
    resetError,
    canDelete,
    hasChildren,
    childrenCount,
    folderTitle
  } = useDeleteFolder(folder, () => {
    onSuccess?.();
    handleClose();
  });

  // Reset error when modal opens
  useEffect(() => {
    if (show) {
      resetError();
    }
  }, [show, resetError]);

  const handleDelete = async () => {
    if (!canDelete || isDeleting) {
      return;
    }

    const success = await deleteFolder();
    if (success) {
      // Modal will be closed by the onSuccess callback in useDeleteFolder
    }
    // If deletion fails, the error will be handled by the hook
    // and the modal stays open to show the error
  };

  const handleModalClose = () => {
    if (!isDeleting) {
      resetError();
      handleClose();
    }
  };

  // Create a custom title for the delete modal
  const deleteTitle = `Delete Folder: ${folderTitle}`;

  return (
    <>
      <DeleteModal
        show={show}
        handleClose={handleModalClose}
        handleDelete={handleDelete}
        title={deleteTitle}
      >
        {/* Show warning if folder has children */}
        {hasChildren && (
          <div
            className='alert alert-warning'
            role='alert'
          >
            <strong>⚠️ Warning:</strong> This folder contains{' '}
            {childrenCount.folders} subfolder(s) and {childrenCount.activities}{' '}
            activity(ies). Deleting this folder will also delete all its
            contents.
          </div>
        )}
      </DeleteModal>

      {/* Show error message if deletion fails */}
      {error && (
        <div
          className='position-fixed top-50 start-50 translate-middle'
          style={{ zIndex: 1060 }}
        >
          <div
            className='alert alert-danger shadow'
            role='alert'
          >
            <strong>Error:</strong> {error}
            <button
              type='button'
              className='btn-close ms-2'
              aria-label='Close'
              onClick={resetError}
            ></button>
          </div>
        </div>
      )}
    </>
  );
};

export default DeleteFolderModal;
